/**
 * The portal's illustration vocabulary.
 *
 * Same engraved line-art the marketing site uses for its feature and reason
 * cards (`apps/website/public/art/**`) — isometric objects, ink on white paper,
 * no colour. The files are copies rather than a shared package because the two
 * apps deploy separately and each serves its own `public/`.
 *
 * Rendered through <Mark>, which multiplies the white ground away so the ink
 * sits directly on whatever surface it lands on.
 *
 * @see apps/website/components/site/features.tsx — the source of the language.
 */

export const MARKS = {
  websites: '/art/websites.png',
  leads: '/art/leads.png',
  bookings: '/art/bookings.png',
  content: '/art/content.png',
  reviews: '/art/reviews.png',
  seo: '/art/seo.png',
  analytics: '/art/analytics.png',
  activity: '/art/activity.png',
  notifications: '/art/notifications.png',
  support: '/art/support.png',
  domain: '/art/domain.png',
  /** Upsell subjects — the feature itself, never a padlock. */
  autoContent: '/art/auto-content.png',
  reviewEngine: '/art/review-engine.png',
  /** Invite-side subjects — what the visitor is being shown, not a dashboard tab. */
  multipage: '/art/multipage.png',
  clickToCall: '/art/click-to-call.png',
  domainSsl: '/art/domain-ssl.png',
  /**
   * Subjects for the screens outside the dashboard — the ones a customer hits
   * before signing in or when something goes wrong. Each draws the situation
   * rather than a status glyph: a key turned in a lock, a stamp that certifies
   * the address, a signpost at a fork, a blown fuse, a spiked receipt.
   */
  signIn: '/art/signin.png',
  verified: '/art/verified.png',
  wrongTurn: '/art/signpost.png',
  broken: '/art/fuse.png',
  receipts: '/art/receipt.png',
  /**
   * The four "what the monthly actually pays for" cards on the invite page.
   * Each draws the promise, not a dashboard feature: ring and a person
   * appears, we do the editing, machinery that runs unseen, being findable.
   */
  answers: '/art/desk-bell.png',
  edits: '/art/pencil.png',
  upkeep: '/art/gears.png',
  found: '/art/magnifier.png',
} as const;

export type MarkName = keyof typeof MARKS;

/**
 * One drawing per trade, keyed by the output of `tradeName()` in
 * components/invite/kit.tsx — the five verticals the generator actually covers.
 * An unrecognised trade has no object worth drawing, so it gets nothing rather
 * than a generic stand-in.
 */
export const TRADE_MARKS: Record<string, string> = {
  plumbing: '/art/trades/t-plumbers.png',
  roofing: '/art/trades/t-roofers.png',
  HVAC: '/art/trades/t-hvac.png',
  electrical: '/art/trades/t-electricians.png',
  landscaping: '/art/trades/t-landscapers.png',
};

/** The three beats of what happens after someone claims a site. */
export const PROCESS_MARKS = {
  form: '/art/process/s-form.png',
  build: '/art/process/s-build.png',
  launch: '/art/process/s-launch.png',
} as const;
